// FlashListener.js
/**
 * FlashListener
 * -------------
 * Listens for global "flashEvent" events and displays messages as either
 * Bootstrap toasts or inline alerts.
 *
 * This class acts as the **orchestrator** between:
 * - ToastNotifier (for toast notifications)
 * - InlineAlertNotifier (for inline alerts)
 *
 * Responsibilities:
 * - Listen for `flashEvent` dispatched on `document`
 * - Decide whether to display a toast or an inline alert
 * - Forward the message to the appropriate notifier
 * - Handle per-event options such as dismiss time and HTML rendering
 *
 * Flash events should be dispatched as a CustomEvent with a `detail` object.
 *
 * Supported modes:
 * - "toast"  → show a toast notification
 * - "inline" → show an alert inside the event target element
 *
 * If `mode` is not specified or the target element is invalid,
 * the listener falls back to showing a toast.
 *
 * Event detail options:
 * - type        : alert type (success, danger, warning, info, etc.)
 * - message     : message to display
 * - mode        : "toast" or "inline"
 * - allowHtml   : allow sanitized HTML in the message
 * - dismissMs   : override auto-dismiss timeout
 *
 * Example usage:
 *
 * import FlashListener from './FlashListener.js';
 * import ToastNotifier from './ToastNotifier.js';
 * import InlineAlertNotifier from './InlineAlertNotifier.js';
 * import DOMPurify from 'dompurify';
 *
 * const domPurify = DOMPurify(window);
 *
 * const toastNotifier = new ToastNotifier({ domPurify });
 * const inlineNotifier = new InlineAlertNotifier({ domPurify });
 *
 * const flashListener = new FlashListener({
 *     toastNotifier,
 *     inlineAlertNotifier: inlineNotifier,
 *     autoDismissMs: 6000
 * });
 *
 * flashListener.install();
 *
 * Example: trigger a toast
 *
 * document.dispatchEvent(new CustomEvent('flashEvent', {
 *     detail: {
 *         type: 'success',
 *         message: 'Salvato con successo!',
 *         mode: 'toast',
 *         dismissMs: 3000
 *     }
 * }));
 *
 * Example: trigger an inline alert
 *
 * const container = document.querySelector('#result-container');
 *
 * container.dispatchEvent(new CustomEvent('flashEvent', {
 *     bubbles: true,
 *     detail: {
 *         type: 'warning',
 *         message: 'Controlla i dati inseriti.',
 *         mode: 'inline'
 *     }
 * }));
 */
class FlashListener {
    constructor({
        toastNotifier,
        inlineAlertNotifier,
        autoDismissMs = 6000,
    } = {}) {
        if (!toastNotifier) throw new Error('FlashListener: toastNotifier is required');
        if (!inlineAlertNotifier) throw new Error('FlashListener: inlineAlertNotifier is required');

        this.toastNotifier = toastNotifier;
        this.inlineAlertNotifier = inlineAlertNotifier;
        this.autoDismissMs = autoDismissMs;

        this._installed = false;
        this._boundOnFlashEvent = this._onFlashEvent.bind(this);
    }

    install() {
        if (this._installed) return;
        document.addEventListener('flashEvent', this._boundOnFlashEvent);
        this._installed = true;
    }

    uninstall() {
        if (!this._installed) return;
        document.removeEventListener('flashEvent', this._boundOnFlashEvent);
        this._installed = false;
    }

    _onFlashEvent(e) {
        const data = (e && e.detail) ? e.detail : {};
        const type = data.type || 'info';
        const message = data.message || '';
        const mode = String(data.mode || '').toLowerCase();
        const allowHtml = !!data.allowHtml;

        let dismissMs = (typeof data.dismissMs === 'number') ? data.dismissMs : this.autoDismissMs;
        if (dismissMs < 0) dismissMs = 0;

        if (mode === 'toast') {
            this.toastNotifier.show({
                type,
                message,
                allowHtml,
                dismissMs,
            });
            return;
        }

        const target = e && e.target;
        if (!(target && target.nodeType === 1)) {
            this.toastNotifier.show({
                type,
                message,
                allowHtml,
                dismissMs,
            });
            return;
        }

        this.inlineAlertNotifier.show(target, {
            type,
            message,
            allowHtml,
            dismissMs,
        });
    }
}

export default FlashListener;
